import { FC, useMemo, useState } from "react";
import { Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Log } from "@/types/logBook";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import WMSAvatar from "@/styles/WMSAvatar";
import SText from "@/styles/SText";
import { dateFormat } from "@/utils/format";
import iconSystem from "@/assets/images/common/system.svg";
import { LogItemWrap } from "../../style";
import LogAttachments from "./LogAttachments";

const { Paragraph } = Typography;

interface LogItemProps {
  log: Log;
  replyExpanded?: boolean;
  onReply?: (log: Log) => void;
  onToggleReplies?: (parentLogId: string) => void;
}

const LogItem: FC<LogItemProps> = ({
  log,
  replyExpanded,
  onReply,
  onToggleReplies,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isSystemLog = log.createdBy === "SYSTEM";

  const subLogs = useMemo(
    () =>
      [...(log.subs ?? [])].sort(
        (leftLog, rightLog) => (leftLog.createdAt ?? 0) - (rightLog.createdAt ?? 0),
      ),
    [log.subs],
  );

  const getReplyUserName = (currentLog: Log) => {
    if (currentLog.createdBy === "SYSTEM") {
      return "SYSTEM";
    }
    if (!currentLog.creator) {
      return "-";
    }
    return getUserName(currentLog.creator);
  };

  const renderAttachments = (currentLog: Log) =>
    currentLog.docId || currentLog.doc ? (
      <div className="log-item--attachments">
        <LogAttachments
          docId={currentLog.docId}
          doc={currentLog.doc}
          displayMode="list"
        />
      </div>
    ) : null;

  return (
    <LogItemWrap>
      <div className="log-item--header">
        <div className="log-item--header--user">
          {isSystemLog && (
            <img alt="" src={iconSystem} style={{ width: 30, height: 30 }} />
          )}
          {!isSystemLog && log.creator && (
            <WMSAvatar
              alt={getUserAvatarTip(log.creator)}
              uniChar={log.creator.id}
              avatarId={log.creator.avatar}
              size={30}
              creator={log.creator}
              style={{ minWidth: 30 }}
            />
          )}
          <div className="log-item--header--user-name">
            {isSystemLog && <span>[SYSTEM]</span>}
            {!isSystemLog && log.creator && (
              <SText text={getUserName(log.creator)} />
            )}
          </div>
        </div>
        <div className="log-item--header--meta">
          {!isSystemLog && (
            <div className="log-item--header--actions">
              <button
                type="button"
                className="log-item--action"
                onClick={() => onReply?.(log)}
              >
                {t("components.reply")}
              </button>
            </div>
          )}
          <div className="log-item--header--time">
            {dateFormat(dayjs(log.createdAt), true)}
          </div>
        </div>
      </div>

      <div className="log-item--body">
        <div className="log-item--content">
          <Paragraph
            ellipsis={{
              rows: 4,
              expandable: "collapsible",
              expanded,
              onExpand: (_, info) => setExpanded(info.expanded),
            }}
          >
            {log.content}
          </Paragraph>
        </div>
        {renderAttachments(log)}

        {subLogs.length > 0 && (
          <div className="log-item--replies">
            <button
              type="button"
              className="log-item--replies-toggle"
              onClick={() => onToggleReplies?.(log.id)}
            >
              {replyExpanded
                ? t("components.collapseReplies")
                : t("components.replyCount", { name: subLogs.length })}
            </button>

            {replyExpanded && (
              <div className="log-item--replies-list">
                {subLogs.map((subLog) => {
                  const replyName = getReplyUserName(subLog);

                  return (
                    <div key={subLog.id} className="log-item--reply-row">
                      <div className="log-item--reply-row-header">
                        <div className="log-item--reply-row-title">
                          <span>{replyName}</span>
                          <span style={{ color: "rgb(169, 173, 186)" }}>
                            {dateFormat(dayjs(subLog.createdAt), true)}
                          </span>
                        </div>
                      </div>
                      <div className="log-item--reply-row-content">{subLog.content}</div>
                      {renderAttachments(subLog)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </LogItemWrap>
  );
};

export default LogItem;
